
import { defineComponent, reactive, Ref, ref, toRaw, watchEffect } from 'vue';
import {
  CreditCardOutlined,
  DollarCircleOutlined,
  // FileProtectOutlined,
} from '@ant-design/icons-vue';
import CardForm from '@/components/CardForm.vue';
// import ECheckForm from '@/components/ECheckForm.vue';
import { useForm } from '@ant-design-vue/use';
import { PaymentFormType } from '@/types/payment-form.type';
import { useIntegrations, useGlobalProps } from '@/composables';
import { IntegrationsService } from '@/services';
import { Validator } from '@/utils/ant-custom-validators';

interface State {
  type: PaymentFormType;
  autopay: boolean;
  accountId: string | null;
}

export default defineComponent({
  components: {
    CardForm,
    // ECheckForm,
    CreditCardOutlined,
    DollarCircleOutlined,
    // FileProtectOutlined,
  },
  setup() {
    const { setPaymentMethodSelected, form } = useGlobalProps();
    const refCardForm = ref<typeof CardForm>() as Ref<typeof CardForm>;
    // const refECheckForm = ref<typeof ECheckForm>() as Ref<typeof ECheckForm>;

    const { hasLawpayIntegration, accounts, setAccounts } = useIntegrations();

    const state = reactive<State>({
      type: 'credit_card',
      autopay: false,
      accountId: null,
    });

    const rules = reactive({
      accountId: [
        {
          validator: Validator.isRequired(),
          trigger: 'change',
        },
      ],
    });

    const primaAccounts = ref<any[]>([]);

    /** Form validation result (AntDesign) */
    const { validateInfos, validate } = useForm(state, rules);

    /** ----- Methods ------ */
    const filterOption = (input: string, option: any) => {
      return option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    /** Validate form and return data */
    const validateForm = async () => {
      if (state.type === 'credit_card') {
        // Credit card mode (lawpay)
        try {
          const lawPayRef = refCardForm;
          // const lawPayRef =
          //   state.type === 'credit_card' ? refCardForm : refECheckForm;

          if (!lawPayRef.value.savedPaymentMethod.id) {
            const [, layPawIsDone] = await Promise.all([
              validate('accountId'),
              lawPayRef.value.validateFn(),
            ]);

            if (!layPawIsDone) {
              throw 'Invalid form fields';
            }
          } else {
            const accountIsDone = await Promise.all([validate('accountId')]);

            if (!accountIsDone) {
              throw 'Invalid account field';
            }
          }

          // Validation successfully
          const lawpay = await lawPayRef.value.lawPayRequest();
          return Promise.resolve({ ...toRaw(state), lawpay });
        } catch (error) {
          return Promise.reject(error);
        }
      } else {
        // Cash mode
        let done = true;

        // Validate other fields
        try {
          await validate();
        } catch (error) {
          done = false;
        }

        if (!done) {
          return Promise.reject(new Error('Invalid form values'));
        }

        return Promise.resolve(toRaw(state));
      }
    };

    const setStateType = () => {
      if (!hasLawpayIntegration.value) {
        state.type = 'cash';
        return;
      }
    };

    /** Execute request on create component */
    const executeRequests = async () => {
      if (accounts.value.length) return;

      // Get accounts
      try {
        primaAccounts.value = await IntegrationsService.getAccounts();
        setAccounts(primaAccounts.value);
        setStateType();
      } catch (error) {
        console.error(error);
      }
    };

    executeRequests();

    const alternAccounts = () => {
      state.accountId = null;

      if (state.type === 'credit_card') {
        setAccounts(
          primaAccounts.value.filter(
            account =>
              account.lawPayAccount !== null &&
              account.lawPayAccount.some(
                (lawPayAccount: any) => lawPayAccount.type === 'card',
              ),
          ),
        );
      } else setAccounts(primaAccounts.value);

      return;
    };

    watchEffect(() => {
      alternAccounts();

      setPaymentMethodSelected(state.type);
    });

    return {
      state,
      accounts,
      validateInfos,
      hasLawpayIntegration,
      filterOption,
      validateForm,
      refCardForm,
      alternAccounts,
      primaAccounts,
      setStateType,
      // refECheckForm,
      form,
    };
  },
});
